import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { AsyncJobType } from '@recruitee/account-data';
import { AsyncJobService } from '@recruitee/async-job-data';
import { HttpApiService } from '@recruitee/http';
import {
  CandidateReferrersReference,
  FullCandidate,
  OfferBriefReference,
  ShareCandidateWithTeamResponse,
  StageReference,
  TalentPoolBriefReference,
  UserReference,
} from '@recruitee/http-types';
import { getOptions } from '@recruitee/walkthrough-utils';

export interface CopyToCompanyParams {
  id: number;
  offerId: number | null;
  talentPoolId: number | null;
  companyId: number;
  locationIds: number[] | null;
}

export interface DeleteParsedCvParams {
  id: number;
  deleteParsedData: boolean;
}

export interface UpdateFileParams {
  id: number;
  file: File;
}

export interface UpdateParseCvParams extends UpdateFileParams {
  parse: any;
}

export interface SaveParams {
  data: Partial<FullCandidate>;
  offers?: Array<{ id: number; locationIds?: number[] | null }> | null;
  talentPools?: number[] | null;
}

export type FullCandidateReferences =
  | OfferBriefReference
  | TalentPoolBriefReference
  | StageReference
  | UserReference
  | CandidateReferrersReference;

export interface FullCandidateResponse {
  candidate: FullCandidate;
  references: FullCandidateReferences[];
}

export interface ShareCandidateWithTeamRequest {
  candidateId: number;
  adminIds: number[];
  placementId: number;
  message: Record<string, unknown>;
}

@Injectable({ providedIn: 'root' })
export class FullCandidateHttpClient {
  constructor(
    protected httpApi: HttpApiService,
    protected asyncJob: AsyncJobService,
  ) {}

  public create({
    data,
    offers = null,
    talentPools = null,
  }: SaveParams): Observable<FullCandidateResponse> {
    return this.httpApi.post(`/candidates`, {}, { candidate: data, offers, talentPools });
  }

  public update({
    data,
    offers = null,
    talentPools = null,
  }: SaveParams): Observable<FullCandidateResponse> {
    return this.httpApi.patch(
      `/candidates/${data.id}`,
      {},
      { candidate: data, offers, talentPools },
    );
  }

  public updateCv({ id, file }: UpdateFileParams): Observable<FullCandidateResponse> {
    const formData = new FormData();
    formData.append('candidate[cv]', file);

    return this.httpApi.patch(`/candidates/${id}/update_cv`, {}, formData);
  }

  public updateParseCv({
    id,
    file,
    parse,
  }: UpdateParseCvParams): Observable<FullCandidateResponse> {
    const formData = new FormData();
    formData.append('candidate[cv]', file);
    formData.append('parse', parse);

    return this.httpApi.patch(`/candidates/${id}/update_cv`, {}, formData);
  }

  public deleteParsedCv({
    id,
    deleteParsedData,
  }: DeleteParsedCvParams): Observable<FullCandidateResponse> {
    return this.httpApi.delete(
      `/candidates/${id}/delete_cv?delete_parsed_data=${deleteParsedData}`,
    );
  }

  public fetchOne(id: number): Observable<FullCandidateResponse> {
    return this.httpApi.get(`/candidates/${id}`, null, getOptions({ hasOnboarding: true }));
  }

  public removePhoto(id: number): Observable<FullCandidateResponse> {
    return this.httpApi.patch(`/candidates/${id}/remove_photo`);
  }

  public deleteCv(id: number): Observable<FullCandidateResponse> {
    return this.httpApi.delete(`/candidates/${id}/delete_cv`);
  }

  public reparseCv(id: number): Observable<FullCandidateResponse> {
    return this.httpApi.post(`/candidates/${id}/parse_cv`);
  }

  public view(id: number): Observable<FullCandidateResponse> {
    return this.httpApi.patch(`/candidates/${id}/view`);
  }

  public follow(id: number): Observable<FullCandidateResponse> {
    return this.httpApi.patch(`/candidates/${id}/follow`);
  }

  public unfollow(id: number): Observable<FullCandidateResponse> {
    return this.httpApi.patch(`/candidates/${id}/unfollow`);
  }

  public retrieve(id: number): Observable<FullCandidateResponse> {
    return this.httpApi.post(`/candidates/${id}/retrieve`);
  }

  public merge(id: number): Observable<{ uuid: string }> {
    return this.httpApi
      .patch(`/candidates/${id}/merge`)
      .pipe(switchMap(this.asyncJob.trackAsyncJobProgress(AsyncJobType.CandidateMerge)));
  }

  public copyToCompany({
    id,
    offerId,
    talentPoolId,
    companyId,
    locationIds,
  }: CopyToCompanyParams): Observable<FullCandidateResponse> {
    return this.httpApi.patch(
      `/candidates/${id}/copy_to_company`,
      {},
      { offerId, talentPoolId, toCompanyId: companyId, locationIds },
    );
  }

  public updateCoverLetterFile({
    id,
    file,
  }: {
    id: number;
    file: File;
  }): Observable<FullCandidateResponse> {
    const formData = new FormData();
    formData.append('candidate[coverLetterFile]', file);
    return this.httpApi.patch(`/candidates/${id}/update_cover_letter`, null, formData);
  }

  public deleteCoverLetterFile(id: number): Observable<FullCandidateResponse> {
    return this.httpApi.delete(`/candidates/${id}/delete_cover_letter`);
  }

  public shareWithTeam({
    adminIds,
    placementId,
    message = {},
  }: ShareCandidateWithTeamRequest): Observable<ShareCandidateWithTeamResponse> {
    return this.httpApi.post(
      `/candidates/share/admins`,
      {},
      {
        adminIds,
        placementId,
        messageJson: message,
      },
    );
  }

  public revokeAccess(candidateId: number, userId: number) {
    return this.httpApi.delete(`/candidates/${candidateId}/share/admins?admin_id=${userId}`);
  }
}
